import React, { useEffect, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { Row, Col, Table, CardBody } from 'reactstrap'
import { API_URL } from './utils/Constants/URLS'
import { useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload } from '@fortawesome/free-solid-svg-icons'

const CreateDI = () => {
  const token = localStorage.getItem('token')
  const { soid } = useParams()
  const navigate = useNavigate()
  const userDetails = useSelector(store => store.user)

  const [soData, setSoData] = useState({})
  const [selectedQuantities, setSelectedQuantities] = useState({})
  const [includedLines, setIncludedLines] = useState({})
  const [loadingSo, setLoadingSo] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const getLineId = (line, idx) => line?._id ?? line?.id ?? `line-${idx}`
  const getModelName = line =>
    line?.model?.modelName ??
    line?.modelName ??
    line?.name ??
    (line?.model?._id ? line.model._id : line?.model) ??
    '-'
  const getPartCode = line =>
    line?.model?.partCode ?? line?.partCode ?? line?.part_code ?? '-'

  // NEW: available = quantity - diQuantity (never negative)
  const getAvailableQty = line => {
    const q = Number(line?.quantity ?? 0)
    const diq = Number(line?.diQuantity ?? 0)
    return Math.max(0, q - diq)
  }

  const getDisplayLineItems = so => {
    const soLineItems = Array.isArray(so?.lineItems) ? so.lineItems : []
    const hasModelInfo =
      soLineItems.length > 0 &&
      (soLineItems[0].model ||
        soLineItems[0].listPrice ||
        soLineItems[0].rate ||
        soLineItems[0].value)
    if (hasModelInfo) return soLineItems
    const crossSells = so?.lead?.productRequirements?.crossSells
    if (Array.isArray(crossSells) && crossSells.length > 0) return crossSells
    return soLineItems
  }

  useEffect(() => {
    const fetchSO = async () => {
      setLoadingSo(true)
      try {
        const res = await fetch(`${API_URL}leads/sales-orders/${soid}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        })
        const result = await res.json()
        const data = result?.data || {}
        setSoData(data)

        // Initialize selection/inclusion based on available = quantity - diQuantity
        const itemsForInit = getDisplayLineItems(data)
        const initialQuantities = {}
        const initialIncluded = {}
        if (Array.isArray(itemsForInit)) {
          itemsForInit.forEach((li, idx) => {
            const lineId = getLineId(li, idx)
            const avail = getAvailableQty(li)
            initialQuantities[lineId] = avail
            initialIncluded[lineId] = avail > 0
          })
        }
        setSelectedQuantities(initialQuantities)
        setIncludedLines(initialIncluded)
      } catch (e) {
        alert('Error fetching sales order details')
      } finally {
        setLoadingSo(false)
      }
    }
    if (soid) fetchSO()
  }, [token, soid])

  const downloadFile = async filePath => {
    if (!filePath) {
      alert('File not available')
      return
    }
    try {
      const url = `${API_URL}file/serve`
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ filePath })
      })
      if (!response.ok) {
        alert('Error downloading file')
        return
      }
      const blob = await response.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = filePath.split('/').pop() || 'download'
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(downloadUrl)
    } catch (e) {
      alert('Error downloading file')
    }
  }

  const handleQuantityChange = (lineId, value, available) => {
    const numeric = Number(value || 0)
    const max = Number(available ?? 0)
    if (numeric > max) {
      alert(`Quantity cannot exceed ${max}`)
      return
    }
    setSelectedQuantities(prev => ({ ...prev, [lineId]: numeric }))
  }

  const handleToggleInclude = lineId => {
    setIncludedLines(prev => ({ ...prev, [lineId]: !prev[lineId] }))
  }

  const displayLineItems = getDisplayLineItems(soData)

  const allIncluded =
    Array.isArray(displayLineItems) &&
    displayLineItems.length > 0 &&
    displayLineItems.every((li, idx) => !!includedLines[getLineId(li, idx)])

  const handleToggleSelectAll = () => {
    const itemsArr = Array.isArray(displayLineItems) ? displayLineItems : []
    const newState = !allIncluded
    const updated = {}
    itemsArr.forEach((li, idx) => {
      updated[getLineId(li, idx)] = newState
    })
    setIncludedLines(prev => ({ ...prev, ...updated }))
  }

  const computeFooterTotals = () => {
    let totalQty = 0
    let totalValue = 0
    const itemsArr = Array.isArray(displayLineItems) ? displayLineItems : []
    itemsArr.forEach((li, idx) => {
      const lineId = getLineId(li, idx)
      if (!includedLines[lineId]) return
      const qty = Number(selectedQuantities[lineId] ?? 0)
      totalQty += qty
      const unit = Number(li.rate ?? li.listPrice ?? 0)
      totalValue += unit * qty
    })
    return { totalQty, totalValue }
  }

  const { totalQty, totalValue } = computeFooterTotals()

  const handleCreateDI = async () => {
    const itemsArrForDisplay = Array.isArray(displayLineItems)
      ? displayLineItems
      : []

    // Prepare the items to include in DI creation
    const diItems = itemsArrForDisplay
      .map((li, idx) => {
        const lineId = getLineId(li, idx)
        const qty = Number(selectedQuantities[lineId] ?? 0)

        return {
          lineId,
          model: li?.model?._id ?? li?.model ?? null,
          quantity: qty,
          listPrice: Number(li?.listPrice ?? 0),
          discount: Number(li?.discount ?? 0),
          rate: Number(li?.rate ?? 0),
          value: Number(li?.rate ?? 0) * qty,
          standardLeadTime: li?.standardLeadTime ?? null,
          committedDeliveryDate: li?.committedDeliveryDate ?? null
        }
      })
      .filter(it => includedLines[it.lineId] && it.quantity > 0)

    if (diItems.length === 0) {
      alert('Please include at least one line item with quantity > 0')
      return
    }

    // ✅ FIXED: Work with actual SO lineItems, not displayLineItems
    const actualSoLineItems = Array.isArray(soData?.lineItems)
      ? soData.lineItems
      : []

    // Update diQuantity in the actual SO lineItems
    const updatedSoLineItems = actualSoLineItems.map(orig => {
      const origLineId = getLineId(orig, actualSoLineItems.indexOf(orig))
      const origModelId =
        orig?.model?._id ??
        (typeof orig?.model === 'string' ? orig.model : null)

      // Find matching DI item by lineId first, then by model
      const matched = diItems.find(d => {
        // Try matching by lineId
        if (d.lineId === origLineId) return true

        // Try matching by _id
        if (d.lineId && orig?._id && String(d.lineId) === String(orig._id)) {
          return true
        }

        // Try matching by model ID
        if (d.model && origModelId) {
          return String(d.model) === String(origModelId)
        }

        return false
      })

      if (!matched) return orig

      const selectedQty = Number(matched.quantity || 0)
      const currentQty = Number(orig.quantity ?? 0)
      const currentDiQty = Number(orig.diQuantity ?? 0)

      // Accumulate diQuantity
      const newDiQty = Math.min(currentDiQty + selectedQty, currentQty)

      return {
        ...orig,
        diQuantity: newDiQty
      }
    })

    setSubmitting(true)
    try {
      // PATCH the SO to update diQuantity per line
      const patchPayload = {
        lineItems: updatedSoLineItems,
        status: soData?.status ?? 'pending'
      }

      const soPatchRes = await fetch(`${API_URL}leads/sales-orders/${soid}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(patchPayload)
      })
      const soPatchResult = await soPatchRes.json()

      if (!(soPatchRes.ok && soPatchResult && soPatchResult.error === false)) {
        alert(
          soPatchResult?.message || 'Error updating Sales Order allocations'
        )
        setSubmitting(false)
        return
      }

      // Create the DI with selected quantities
      const diPayload = {
        salesOrderNumber: soData?.salesOrderNumber,
        lineItems: diItems.map(i => ({
          model: i.model,
          quantity: i.quantity,
          listPrice: i.listPrice,
          discount: i.discount,
          rate: i.rate,
          value: i.value,
          standardLeadTime: i.standardLeadTime,
          committedDeliveryDate: i.committedDeliveryDate
        }))
      }

      if (soData?.isPOAvailable === true && soData?.customerPONumber) {
        diPayload.customerPONumber = soData.customerPONumber
      }

      const diRes = await fetch(`${API_URL}delivery-instructions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(diPayload)
      })
      const diResult = await diRes.json()

      if (!(diRes.ok && diResult && diResult.error === false)) {
        alert(diResult?.message || 'Error creating Delivery Instruction')
        setSubmitting(false)
        return
      }

      // Update local state with the updated lineItems
      setSoData(prev => ({ ...prev, lineItems: updatedSoLineItems }))

      alert('Delivery Instruction created and Sales Order updated successfully')
      navigate('/invoice_journey_start')
    } catch (e) {
      console.error('Error:', e)
      alert('Network error while creating DI / updating SO')
    } finally {
      setSubmitting(false)
    }
  }
  const formatDate = date =>
    date
      ? new Date(date).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
      : '-'

  return (
    <>
      <CardBody className='p-4'>
        <Row>
          <Col md={4}>
            <label className='form-label'>
              <strong>Sales Order Number</strong>
            </label>
            <p>{soData?.salesOrderNumber || '-'}</p>
          </Col>
          <Col md={4}>
            <label className='form-label'>
              <strong>Business Line</strong>
            </label>
            <p>{soData?.businessLine?.name || '-'}</p>
          </Col>
          <Col md={4}>
            <label className='form-label'>
              <strong>Lead No</strong>
            </label>
            <p>{soData?.lead?.leadId || '-'}</p>
          </Col>
          <Col md={4}>
            <label className='form-label'>
              <strong>Sales Executive</strong>
            </label>
            <p>{soData?.lead?.leadOwner?.name || '-'}</p>
          </Col>
          <Col md={4}>
            <label className='form-label'>
              <strong>Customer Name</strong>
            </label>
            <p>{soData?.lead?.client?.companyName || '-'}</p>
          </Col>
          <Col md={4}>
            <label className='form-label'>
              <strong>Date of SO</strong>
            </label>
            <p>{formatDate(soData?.createdAt)}</p>
          </Col>
          {soData?.isPOAvailable === true ? (
            <>
              <Col md={4}>
                <label className='form-label'>
                  <strong>Customer PO Number</strong>
                </label>
                <p>
                  <Link
                    onClick={() => downloadFile(soData?.POFilePath)}
                    className='text-dark no-underline'
                  >
                    {soData?.customerPONumber || '-'}
                  </Link>
                </p>
              </Col>
              <Col md={4}>
                <label className='form-label'>
                  <strong>Customer PO Date</strong>
                </label>
                <p>{formatDate(soData?.customerPODate)}</p>
              </Col>
            </>
          ) : (
            <Col md={8}>
              <label className='form-label'>
                <strong>Po Details</strong>
              </label>
              <p>{soData?.PODetails || '-'}</p>
            </Col>
          )}
          <Col md={4}>
            <label className='form-label'>
              <strong>GSPL GSTIN</strong>
            </label>
            <p>{soData?.gsplGSTNumber || '-'}</p>
          </Col>
          <Col md={4}>
            <label className='form-label'>
              <strong>Consultant (If Any)</strong>
            </label>
            <p>{soData?.consultant || '-'}</p>
          </Col>
          <Col md={4}>
            <label className='form-label'>
              <strong>Currency of Transaction</strong>
            </label>
            <p>{soData?.currencyOfTransaction || '-'}</p>
          </Col>
          <Col md={4}>
            <label className='form-label'>
              <strong>Warranty Period from date of commissioning</strong>
            </label>
            <p>{soData?.warrantyPeriod || '-'} Months</p>
          </Col>
          <Col md={4}>
            <label className='form-label'>
              <strong>Warranty Period from date of last supply</strong>
            </label>
            <p>{soData?.warrantyTerms || '-'} Months</p>
          </Col>
          <Col md={4}>
            <label className='form-label'>
              <strong>Billing Address</strong>
            </label>
            <p>{soData?.billingAddress || '-'}</p>
          </Col>
          <Col md={4}>
            <label className='form-label'>
              <strong>Shipping Address</strong>
            </label>
            <p>{soData?.shippingAddress || '-'}</p>
          </Col>
          <Col md={4}>
            <label className='form-label'>
              <strong>Remarks</strong>
            </label>
            <p>{soData?.remarks || '-'}</p>
          </Col>
          <Col md={4}>
            <label className='form-label'>
              <strong>BOM</strong>
            </label>
            <p>
              <button
                type='button'
                className='btn download-btn mr-2'
                onClick={() => downloadFile(soData?.BOMFilePath)}
              >
                <FontAwesomeIcon icon={faDownload} />
              </button>
            </p>
          </Col>
        </Row>

        <div className='avoid-page-break mt-3'>
          <Table className='so-table' responsive>
            <thead>
              <tr>
                {userDetails?.role === 'commercial_executive' ? (
                  <th className='text-center'>
                    <div className='d-flex flex-column align-items-center' style={{ gap: '4px' }}>
                      <input
                        type='checkbox'
                        checked={allIncluded}
                        onChange={handleToggleSelectAll}
                        title={allIncluded ? 'Deselect All' : 'Select All'}
                        style={{ cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '10px', whiteSpace: 'nowrap', color: '#666' }}>
                        {allIncluded ? 'Deselect All' : 'Select All'}
                      </span>
                    </div>
                  </th>
                ) : (
                  ''
                )}
                <th>SI</th>
                <th>Name</th>
                <th>Part Code</th>
                <th>UOM</th>
                <th>QTY</th>
                <th>Std. Lead Time</th>
                <th>Date</th>
                <th>LP</th>
                <th>Discount</th>
                <th>Rate Per Unit</th>
                <th>Order Value</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(displayLineItems) &&
                displayLineItems.length > 0 ? (
                displayLineItems.map((data, index) => {
                  const lineId = getLineId(data, index)
                  const available = getAvailableQty(data) // NEW
                  const currentSelected = Number(
                    selectedQuantities[lineId] ?? 0
                  )
                  const included = !!includedLines[lineId]
                  const qtyOptions =
                    available >= 0
                      ? Array.from({ length: available + 1 }, (_, i) => i)
                      : [0]
                  return (
                    <tr key={lineId}>
                      {userDetails?.role === 'commercial_executive' ? (
                        <td className='text-center'>
                          <input
                            type='checkbox'
                            checked={included}
                            onChange={() => handleToggleInclude(lineId)}
                          />
                        </td>
                      ) : (
                        ''
                      )}

                      <td>{index + 1}</td>
                      <td>{getModelName(data)}</td>
                      <td>{getPartCode(data)}</td>
                      <td>{data?.model?.uom ?? data?.uom ?? '-'}</td>

                      {userDetails?.role === 'commercial_executive' ? (
                        <td>
                          {available >= 0 ? (
                            <>
                              <select
                                className='form-select'
                                value={currentSelected}
                                onChange={e =>
                                  handleQuantityChange(
                                    lineId,
                                    e.target.value,
                                    available
                                  )
                                }
                                disabled={!included}
                              >
                                {qtyOptions.map(q => (
                                  <option key={q} value={q}>
                                    {q}
                                  </option>
                                ))}
                              </select>
                              <div className='small text-muted mt-1'>
                                Allocated: {Number(data?.diQuantity ?? 0)} /{' '}
                                {Number(data?.quantity ?? 0)} | Pending:{' '}
                                {available}
                              </div>
                            </>
                          ) : (
                            <span className='text-muted'>0</span>
                          )}
                        </td>
                      ) : (
                        <td>{data?.quantity}</td>
                      )}

                      <td>{data?.standardLeadTime ?? '-'}</td>
                      <td>{formatDate(data?.committedDeliveryDate)}</td>
                      <td>{(Number(data?.listPrice) || 0).toFixed(2)}</td>
                      <td>{(Number(data?.discount) || 0).toFixed(2)}%</td>
                      <td>{(Number(data?.rate) || 0).toFixed(2)}</td>
                      <td>{(Number(data?.value) || 0).toFixed(2)}</td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan='12' className='text-center py-4'>
                    No line items found
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                {userDetails?.role === 'commercial_executive' ? <td /> : ''}
                <td colSpan='10' className='text-end pe-3'>Total SO Value</td>
                <td>
                  {(
                    (Array.isArray(displayLineItems) ? displayLineItems : []).reduce(
                      (acc, item) => acc + (Number(item.value) || 0),
                      0
                    )
                  ).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
              </tr>
            </tfoot>
          </Table>
        </div>

        <Row className='mt-3'>
          <Col md={8}>
            <div className='avoid-page-break'>
              <Table className='so-table'>
                <tbody>
                  {Array.isArray(soData?.paymentTerms) &&
                    soData.paymentTerms.length > 0 ? (
                    soData.paymentTerms.map((data, index) => {
                      const totalValueSo = (
                        Array.isArray(displayLineItems) ? displayLineItems : []
                      ).reduce(
                        (acc, item) => acc + (Number(item.value) || 0),
                        0
                      )
                      const computedPercentage = totalValueSo
                        ? (
                          ((Number(data?.amount) || 0) / totalValueSo) *
                          100
                        ).toFixed(2)
                        : '0.00'
                      return (
                        <tr key={index}>
                          <td>{data?.type || '-'}</td>
                          <td>{computedPercentage}%</td>
                          <td>{(Number(data?.amount) || 0).toFixed(2)}</td>

                          {data?.type === 'Others' && (
                            <td>{data?.otherDetails || '-'}</td>
                          )}
                        </tr>
                      )
                    })
                  ) : (
                    <tr>
                      <td colSpan='3' className='text-center py-3'>
                        No payment terms
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              <Table className='so-table'>
                <tbody>
                  <tr>
                    <td>
                      <strong>Price Basis:</strong>
                    </td>
                    <td>{soData?.priceBasis || '-'}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Responsibility of transportation:</strong>
                    </td>
                    <td>{soData?.responsibilityOfTransportation || '-'}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Type of Transaction:</strong>
                    </td>
                    <td>{soData?.typeOfTransaction || '-'}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>GST Details:</strong>
                    </td>
                    <td>GST extra as applicable</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Bank Guarantee Details:</strong>
                    </td>
                    <td>{soData?.bankGuaranteeDetails || '-'}</td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </Col>
        </Row>

        {/* No store selection UI — button only */}
        {userDetails?.role === 'commercial_executive' ? (
          <Row className='mb-3'>
            <Col md={12} className='text-end'>
              <button
                className='btn add-btn text-white'
                type='button'
                onClick={handleCreateDI}
                disabled={submitting}
              >
                {submitting ? 'Creating...' : 'Create Delivery Instruction'}
              </button>
            </Col>
          </Row>
        ) : (
          ''
        )}
      </CardBody>
    </>
  )
}

export default CreateDI
